import React, { useEffect, useState } from "react";
import { Users, ArrowSquareOut } from "@phosphor-icons/react";
import api from "@/api";
import LazyAvatar from "@/components/LazyAvatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * "Meet the DFTB team" - rendered near the top of the Info page so delegates
 * know who's running the conference. Admin-editable via Admin → Team.
 *
 * Grid shows photo + name + role only. Tapping a member opens a modal with
 * their full bio so the page doesn't get dominated by long paragraphs.
 */
export default function TeamSection() {
  const [members, setMembers] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/team")
      .then((r) => { if (alive) setMembers(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (members.length === 0) return null;

  return (
    <section
      data-testid="info-team-section"
      className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm"
    >
      <div className="flex items-center gap-2 mb-1">
        <Users size={18} className="text-[#000000]" weight="duotone" />
        <h2 className="font-display text-lg">The DFTB team</h2>
      </div>
      <p className="text-xs text-[#7A828C] mb-4">
        The volunteer editors and founders behind Don&apos;t Forget the Bubbles &mdash; the people putting this conference together. Tap a name to read more.
      </p>

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="info-team-list">
        {members.map((m) => {
          const expandable = !!(m.bio || m.social_url);
          const Tag = expandable ? "button" : "div";
          return (
            <li key={m.id} data-testid={`info-team-member-${m.id}`}>
              <Tag
                type={expandable ? "button" : undefined}
                onClick={expandable ? () => setActive(m) : undefined}
                className={`w-full text-left flex items-center gap-3 p-3 bg-[#F8F8FB] rounded-sm ${
                  expandable ? "hover:bg-[#E6F8FC] transition-colors" : ""
                }`}
                data-testid={expandable ? `info-team-open-${m.id}` : undefined}
              >
                <Avatar member={m} />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm text-[#1c1c24] leading-tight">{m.name}</div>
                  {m.role && (
                    <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#000000] mt-0.5">
                      {m.role}
                    </div>
                  )}
                </div>
              </Tag>
            </li>
          );
        })}
      </ul>

      <Dialog open={!!active} onOpenChange={(v) => { if (!v) setActive(null); }}>
        <DialogContent className="bg-[#FFFFFF] max-w-md" data-testid="info-team-member-dialog">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <Avatar member={active} />
                  <div className="min-w-0 flex-1">
                    <DialogTitle className="font-display text-xl leading-tight">{active.name}</DialogTitle>
                    {active.role && (
                      <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#000000] mt-1">
                        {active.role}
                      </div>
                    )}
                  </div>
                </div>
              </DialogHeader>
              {active.bio && (
                <p className="text-sm text-[#1c1c24] leading-relaxed whitespace-pre-line">{active.bio}</p>
              )}
              {active.social_url && (
                <a
                  href={active.social_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-sm text-[#000000] hover:underline"
                  data-testid={`info-team-social-${active.id}`}
                >
                  Profile <ArrowSquareOut size={13} />
                </a>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Avatar({ member }) {
  return (
    <LazyAvatar
      endpoint={`/team/${member.id}/photo`}
      hasPhoto={member.has_photo}
      name={member.name}
      className="w-12 h-12 rounded-full flex-shrink-0 border border-[#E5E9F0]"
    />
  );
}
